/**
 * AudioProcessor - Classe utilitaire pour le traitement audio avancé
 * Utilisée par le Studio d'Enregistrement Audio Pro de Voxdub
 */

export class AudioProcessor {
  constructor(audioContext) {
    this.audioContext = audioContext;
    this.nodes = {};
  }

  /**
   * Créer une chaîne de traitement audio complète
   */
  createProcessingChain() {
    // Input Gain
    this.nodes.inputGain = this.audioContext.createGain();
    this.nodes.inputGain.gain.value = 1.0;

    // High-pass filter (remove rumble)
    this.nodes.highPass = this.audioContext.createBiquadFilter();
    this.nodes.highPass.type = 'highpass';
    this.nodes.highPass.frequency.value = 80;
    this.nodes.highPass.Q.value = 0.7;

    // EQ Section
    this.nodes.lowShelf = this.audioContext.createBiquadFilter();
    this.nodes.lowShelf.type = 'lowshelf';
    this.nodes.lowShelf.frequency.value = 100;
    this.nodes.lowShelf.gain.value = 0;

    this.nodes.midPeak1 = this.audioContext.createBiquadFilter();
    this.nodes.midPeak1.type = 'peaking';
    this.nodes.midPeak1.frequency.value = 500;
    this.nodes.midPeak1.Q.value = 0.7;
    this.nodes.midPeak1.gain.value = 0;

    this.nodes.midPeak2 = this.audioContext.createBiquadFilter();
    this.nodes.midPeak2.type = 'peaking';
    this.nodes.midPeak2.frequency.value = 2000;
    this.nodes.midPeak2.Q.value = 0.7;
    this.nodes.midPeak2.gain.value = 0;

    this.nodes.highShelf = this.audioContext.createBiquadFilter();
    this.nodes.highShelf.type = 'highshelf';
    this.nodes.highShelf.frequency.value = 10000;
    this.nodes.highShelf.gain.value = 0;

    // Noise Gate
    this.nodes.noiseGate = this.createNoiseGate();

    // Compressor
    this.nodes.compressor = this.audioContext.createDynamicsCompressor();
    this.nodes.compressor.threshold.value = -24;
    this.nodes.compressor.knee.value = 8;
    this.nodes.compressor.ratio.value = 4;
    this.nodes.compressor.attack.value = 0.003;
    this.nodes.compressor.release.value = 0.25;

    // De-esser (notch filter for sibilance)
    this.nodes.deEsser = this.audioContext.createBiquadFilter();
    this.nodes.deEsser.type = 'peaking';
    this.nodes.deEsser.frequency.value = 7000;
    this.nodes.deEsser.Q.value = 3;
    this.nodes.deEsser.gain.value = 0;

    // Limiter (using waveshaper)
    this.nodes.limiter = this.createLimiter();

    // Output Gain
    this.nodes.outputGain = this.audioContext.createGain();
    this.nodes.outputGain.gain.value = 1.0;

    // Connect the chain
    this.connectChain();

    return this.nodes;
  }

  /**
   * Connecter tous les noeuds dans l'ordre
   */
  connectChain() {
    const chain = [
      this.nodes.inputGain,
      this.nodes.highPass,
      this.nodes.lowShelf,
      this.nodes.midPeak1,
      this.nodes.midPeak2,
      this.nodes.highShelf,
      this.nodes.noiseGate,
      this.nodes.compressor,
      this.nodes.deEsser,
      this.nodes.limiter,
      this.nodes.outputGain
    ];

    for (let i = 0; i < chain.length - 1; i++) {
      chain[i].connect(chain[i + 1]);
    }
  }

  /**
   * Créer un noise gate personnalisé
   */
  createNoiseGate() {
    const gate = this.audioContext.createScriptProcessor(512, 1, 1);
    let isOpen = false;
    const threshold = -40; // dB
    const hysteresis = 6; // dB
    const attack = 0.01; // seconds
    const release = 0.1; // seconds

    gate.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      const output = e.outputBuffer.getChannelData(0);

      for (let i = 0; i < input.length; i++) {
        const inputLevel = 20 * Math.log10(Math.abs(input[i]));

        if (!isOpen && inputLevel > threshold) {
          isOpen = true;
        } else if (isOpen && inputLevel < threshold - hysteresis) {
          isOpen = false;
        }

        output[i] = isOpen ? input[i] : 0;
      }
    };

    return gate;
  }

  /**
   * Créer un limiteur brick-wall
   */
  createLimiter() {
    const limiter = this.audioContext.createWaveShaper();
    const curve = new Float32Array(65536);
    const threshold = 0.95;

    for (let i = 0; i < curve.length; i++) {
      const x = (i / curve.length) * 2 - 1;
      if (Math.abs(x) < threshold) {
        curve[i] = x;
      } else {
        curve[i] = Math.sign(x) * threshold;
      }
    }

    limiter.curve = curve;
    limiter.oversample = '4x';

    return limiter;
  }

  /**
   * Appliquer un preset de traitement
   */
  applyPreset(presetName) {
    const presets = {
      'Voix Claire': {
        lowShelf: { gain: -2 },
        midPeak1: { gain: 1, frequency: 600 },
        midPeak2: { gain: 3, frequency: 2500 },
        highShelf: { gain: 4 },
        compressor: { threshold: -20, ratio: 3 },
        deEsser: { gain: -6 }
      },
      'Voix Chaude': {
        lowShelf: { gain: 4 },
        midPeak1: { gain: 2, frequency: 300 },
        midPeak2: { gain: -1, frequency: 2000 },
        highShelf: { gain: -2 },
        compressor: { threshold: -24, ratio: 4 },
        deEsser: { gain: -3 }
      },
      'Radio': {
        lowShelf: { gain: 2 },
        midPeak1: { gain: 4, frequency: 800 },
        midPeak2: { gain: 5, frequency: 3000 },
        highShelf: { gain: 6 },
        compressor: { threshold: -18, ratio: 6 },
        deEsser: { gain: -8 }
      },
      'Podcast': {
        lowShelf: { gain: 1 },
        midPeak1: { gain: 2, frequency: 500 },
        midPeak2: { gain: 2, frequency: 2000 },
        highShelf: { gain: 2 },
        compressor: { threshold: -22, ratio: 4 },
        deEsser: { gain: -4 }
      },
      'Narration': {
        lowShelf: { gain: 0 },
        midPeak1: { gain: 1, frequency: 400 },
        midPeak2: { gain: 2, frequency: 1500 },
        highShelf: { gain: 1 },
        compressor: { threshold: -26, ratio: 3 },
        deEsser: { gain: -2 }
      }
    };

    const preset = presets[presetName];
    if (!preset) return;

    // Apply EQ settings
    if (preset.lowShelf) {
      this.nodes.lowShelf.gain.value = preset.lowShelf.gain;
    }
    if (preset.midPeak1) {
      this.nodes.midPeak1.gain.value = preset.midPeak1.gain;
      if (preset.midPeak1.frequency) {
        this.nodes.midPeak1.frequency.value = preset.midPeak1.frequency;
      }
    }
    if (preset.midPeak2) {
      this.nodes.midPeak2.gain.value = preset.midPeak2.gain;
      if (preset.midPeak2.frequency) {
        this.nodes.midPeak2.frequency.value = preset.midPeak2.frequency;
      }
    }
    if (preset.highShelf) {
      this.nodes.highShelf.gain.value = preset.highShelf.gain;
    }

    // Apply dynamics settings
    if (preset.compressor) {
      this.nodes.compressor.threshold.value = preset.compressor.threshold;
      this.nodes.compressor.ratio.value = preset.compressor.ratio;
    }

    // Apply de-esser
    if (preset.deEsser) {
      this.nodes.deEsser.gain.value = preset.deEsser.gain;
    }
  }

  /**
   * Analyser le spectre audio en temps réel
   */
  createSpectrumAnalyzer() {
    const analyzer = this.audioContext.createAnalyser();
    analyzer.fftSize = 2048;
    analyzer.smoothingTimeConstant = 0.8;

    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    return {
      analyzer,
      getFrequencyData: () => {
        analyzer.getByteFrequencyData(dataArray);
        return dataArray;
      },
      getTimeDomainData: () => {
        analyzer.getByteTimeDomainData(dataArray);
        return dataArray;
      }
    };
  }

  /**
   * Détection de la saturation
   */
  createClippingDetector() {
    const detector = this.audioContext.createScriptProcessor(512, 1, 1);
    let clippingDetected = false;

    detector.onaudioprocess = (e) => {
      const input = e.inputBuffer.getChannelData(0);
      const output = e.outputBuffer.getChannelData(0);

      for (let i = 0; i < input.length; i++) {
        output[i] = input[i];
        if (Math.abs(input[i]) > 0.99) {
          clippingDetected = true;
        }
      }
    };

    return {
      detector,
      isClipping: () => clippingDetected,
      reset: () => { clippingDetected = false; }
    };
  }

  /**
   * Auto-trim des silences
   */
  async autoTrimAudio(audioBuffer, threshold = -40) {
    const sampleRate = audioBuffer.sampleRate;
    const channelData = audioBuffer.getChannelData(0);
    const minSilenceDuration = 0.1; // seconds
    const minSilenceSamples = minSilenceDuration * sampleRate;

    let startIndex = 0;
    let endIndex = channelData.length - 1;

    // Find start point
    let silenceSamples = 0;
    for (let i = 0; i < channelData.length; i++) {
      const level = 20 * Math.log10(Math.abs(channelData[i]));
      if (level < threshold) {
        silenceSamples++;
      } else {
        if (silenceSamples < minSilenceSamples) {
          startIndex = Math.max(0, i - minSilenceSamples);
        }
        break;
      }
    }

    // Find end point
    silenceSamples = 0;
    for (let i = channelData.length - 1; i >= 0; i--) {
      const level = 20 * Math.log10(Math.abs(channelData[i]));
      if (level < threshold) {
        silenceSamples++;
      } else {
        if (silenceSamples < minSilenceSamples) {
          endIndex = Math.min(channelData.length - 1, i + minSilenceSamples);
        }
        break;
      }
    }

    // Create trimmed buffer
    const trimmedLength = endIndex - startIndex;
    const trimmedBuffer = this.audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      trimmedLength,
      sampleRate
    );

    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const targetData = trimmedBuffer.getChannelData(channel);
      for (let i = 0; i < trimmedLength; i++) {
        targetData[i] = sourceData[startIndex + i];
      }
    }

    return trimmedBuffer;
  }

  /**
   * Normalisation du volume
   */
  normalizeAudio(audioBuffer, targetLevel = -3) {
    const channels = audioBuffer.numberOfChannels;
    let maxLevel = 0;

    // Find max level
    for (let channel = 0; channel < channels; channel++) {
      const data = audioBuffer.getChannelData(channel);
      for (let i = 0; i < data.length; i++) {
        maxLevel = Math.max(maxLevel, Math.abs(data[i]));
      }
    }

    // Calculate gain needed
    const currentLevelDb = 20 * Math.log10(maxLevel);
    const gainDb = targetLevel - currentLevelDb;
    const gain = Math.pow(10, gainDb / 20);

    // Apply gain
    const normalizedBuffer = this.audioContext.createBuffer(
      channels,
      audioBuffer.length,
      audioBuffer.sampleRate
    );

    for (let channel = 0; channel < channels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const targetData = normalizedBuffer.getChannelData(channel);
      for (let i = 0; i < sourceData.length; i++) {
        targetData[i] = Math.max(-1, Math.min(1, sourceData[i] * gain));
      }
    }

    return normalizedBuffer;
  }

  /**
   * Couper une section d'un AudioBuffer
   */
  cutAudio(audioBuffer, startTime, endTime) {
    const sampleRate = audioBuffer.sampleRate;
    const startSample = Math.floor(startTime * sampleRate);
    const endSample = Math.ceil(endTime * sampleRate);
    const cutLength = endSample - startSample;
    const newLength = audioBuffer.length - cutLength;

    if (newLength <= 0) return null;

    const newBuffer = this.audioContext.createBuffer(
      audioBuffer.numberOfChannels,
      newLength,
      sampleRate
    );

    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const targetData = newBuffer.getChannelData(channel);

      // Copy before cut
      targetData.set(sourceData.subarray(0, startSample));
      // Copy after cut
      targetData.set(sourceData.subarray(endSample), startSample);
    }

    return newBuffer;
  }

  /**
   * Insérer ou écraser une section d'un AudioBuffer par un autre
   */
  overwriteAudio(baseBuffer, overlayBuffer, startTime) {
    const sampleRate = baseBuffer.sampleRate;
    const startSample = Math.floor(startTime * sampleRate);
    const overlaySampleLength = overlayBuffer.length;

    // Create a buffer long enough to contain both
    const finalLength = Math.max(baseBuffer.length, startSample + overlaySampleLength);
    const newBuffer = this.audioContext.createBuffer(
      baseBuffer.numberOfChannels,
      finalLength,
      sampleRate
    );

    for (let channel = 0; channel < baseBuffer.numberOfChannels; channel++) {
      const baseData = baseBuffer.getChannelData(channel);
      const overlayData = overlayBuffer.getChannelData(channel);
      const targetData = newBuffer.getChannelData(channel);

      // Copy base data
      targetData.set(baseData);
      // Overlay the new data (overwrite)
      targetData.set(overlayData, startSample);
    }

    return newBuffer;
  }

  /**
   * Mélanger deux AudioBuffers (Mix)
   */
  mergeAudio(buffer1, buffer2) {
    const sampleRate = buffer1.sampleRate;
    const maxLength = Math.max(buffer1.length, buffer2.length);

    const newBuffer = this.audioContext.createBuffer(
      buffer1.numberOfChannels,
      maxLength,
      sampleRate
    );

    for (let channel = 0; channel < buffer1.numberOfChannels; channel++) {
      const data1 = buffer1.getChannelData(channel);
      const data2 = buffer2.getChannelData(channel);
      const targetData = newBuffer.getChannelData(channel);

      for (let i = 0; i < maxLength; i++) {
        const val1 = i < data1.length ? data1[i] : 0;
        const val2 = i < data2.length ? data2[i] : 0;
        targetData[i] = val1 + val2;
      }
    }

    return newBuffer;
  }
}

/**
 * Classe pour la visualisation audio avancée
 */
export class AudioVisualizer {
  constructor(canvas, analyzerNode) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.analyzer = analyzerNode;
    this.animationId = null;
    this.mode = 'waveform'; // 'waveform', 'spectrum', 'spectrogram'
  }

  start() {
    this.draw();
  }

  stop() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  }

  draw() {
    this.animationId = requestAnimationFrame(() => this.draw());

    switch (this.mode) {
      case 'waveform':
        this.drawWaveform();
        break;
      case 'spectrum':
        this.drawSpectrum();
        break;
      case 'spectrogram':
        this.drawSpectrogram();
        break;
    }
  }

  drawWaveform() {
    const bufferLength = this.analyzer.fftSize;
    const dataArray = new Uint8Array(bufferLength);
    this.analyzer.getByteTimeDomainData(dataArray);

    this.ctx.fillStyle = 'rgba(20, 20, 20, 0.1)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = '#42a5f5';
    this.ctx.beginPath();

    const sliceWidth = this.canvas.width / bufferLength;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const v = dataArray[i] / 128.0;
      const y = v * this.canvas.height / 2;

      if (i === 0) {
        this.ctx.moveTo(x, y);
      } else {
        this.ctx.lineTo(x, y);
      }

      x += sliceWidth;
    }

    this.ctx.lineTo(this.canvas.width, this.canvas.height / 2);
    this.ctx.stroke();
  }

  drawSpectrum() {
    const bufferLength = this.analyzer.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this.analyzer.getByteFrequencyData(dataArray);

    this.ctx.fillStyle = 'rgba(20, 20, 20, 0.9)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    const barWidth = (this.canvas.width / bufferLength) * 2.5;
    let barHeight;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      barHeight = (dataArray[i] / 255) * this.canvas.height;

      const r = barHeight + (25 * (i / bufferLength));
      const g = 250 * (i / bufferLength);
      const b = 50;

      this.ctx.fillStyle = `rgb(${r},${g},${b})`;
      this.ctx.fillRect(x, this.canvas.height - barHeight, barWidth, barHeight);

      x += barWidth + 1;
    }
  }

  drawSpectrogram() {
    // Implementation for spectrogram visualization
    // This would require keeping a history of frequency data
    // and displaying it as a heatmap over time
  }

  setMode(mode) {
    this.mode = mode;
  }
} 